// frontend/src/components/LanguageSwitcher.jsx
// =============================================================================
// Runtime EN/PL language switcher.
// =============================================================================
//
// Authenticated runtime language switcher
// ---------------------------------------
// This component is used after login and controls ONLY the effective language of
// the current SPA session. It intentionally does not persist anything to the
// backend user profile and it intentionally does not write to browser storage.
//
// Why this split exists:
// - i18n.language / i18n.resolvedLanguage is the currently displayed UI language,
// - authState.user.preferred_language is the durable profile preference stored
//   in the database,
// - LoginPage may apply a temporary manual language override for the current
//   session even when the profile contains a different language.
//
// Therefore the sidebar switcher must be honest about what the user currently
// sees. Changing it updates the current visible UI immediately, but does not
// silently overwrite the user's profile setting. Durable profile language is
// handled separately from the authenticated user dropdown in Navbar.

import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { normalizeLanguage } from "../i18n";

import "./LanguageSwitcher.css";

const LANGUAGES = [
  { code: "en", shortLabel: "EN", nameKey: "language.english" },
  { code: "pl", shortLabel: "PL", nameKey: "language.polish" },
];

export default function LanguageSwitcher({ compact = false }) {
  const { t, i18n } = useTranslation("common");
  const [changing, setChanging] = useState(false);

  const currentLanguage = useMemo(() => {
    /**
     * The active language in the sidebar is the effective runtime language.
     * Do not derive it from authState.user.preferred_language.
     *
     * Why:
     * - preferred_language is the durable database/profile setting,
     * - the LoginPage may intentionally apply a manual current-session override,
     * - showing the profile language here would make the switcher look stale or
     *   wrong immediately after such a login.
     */
    return normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  }, [i18n.language, i18n.resolvedLanguage]);

  async function changeLanguage(nextLanguage) {
    const lang = normalizeLanguage(nextLanguage);
    if (lang === currentLanguage || changing) return;

    setChanging(true);

    try {
      /**
       * Current-session language change only.
       *
       * This deliberately does NOT call updateMyPreferredLanguage() and does
       * NOT update authState.user.preferred_language. The durable preference is
       * changed only from the dedicated profile-language selector in Navbar or
       * from an explicit user-settings profile screen.
       */
      await i18n.changeLanguage(lang);
    } finally {
      setChanging(false);
    }
  }

  return (
    <div className={`language-switcher ${compact ? "language-switcher--compact" : ""}`}>
      {!compact && <span className="language-switcher__label">{t("language.label")}</span>}
      <div className="language-switcher__buttons" role="group" aria-label={t("language.label")}>
        {LANGUAGES.map((item) => {
          const active = item.code === currentLanguage;
          return (
            <button key={item.code} type="button" className={`language-switcher__btn ${active ? "is-active" : ""}`} onClick={() => changeLanguage(item.code)} disabled={changing} title={t(item.nameKey)} aria-pressed={active}>
              {item.shortLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
}


